import {
	getFourWeeklyDatesInRange,
	getFortnightlyDatesInRange,
	getMonthlyPayDate,
	getWeeklyDatesInRange,
	isWithinRange,
} from './budget';
import { getPayCycleRange } from './month';
import { parseDateOnly, round2 } from './format';

/**
 * A schedule item is anything that recurs on a known cadence inside a
 * pay-cycle range. `IncomeSchedule` and `CategoryLine` both satisfy this
 * shape structurally — keeping the math in one place avoids duplicating
 * the per-frequency branching for income vs. expense schedules.
 */
export interface ScheduleItem {
	amount: number;
	frequency: 'monthly' | 'fortnightly' | 'four-weekly' | 'weekly' | 'one-off';
	monthlyDay?: number;
	fortnightlyPayDate?: string;
	fourWeeklyPayDate?: string;
	oneOffDate?: string;
	weeklyDay?: number;
}

/**
 * Returns every date the item falls on inside `[start, end]` (inclusive).
 * Returns an empty array when the item lacks the field its frequency
 * requires (e.g. weekly without a weeklyDay) or when the configured
 * date sits outside the range.
 */
export function occurrencesInRange(item: ScheduleItem, start: Date, end: Date): Date[] {
	if (item.frequency === 'monthly') {
		const day = item.monthlyDay ?? 15;
		const payDate = getMonthlyPayDate(start, day);
		return isWithinRange(payDate, start, end) ? [payDate] : [];
	}

	if (item.frequency === 'fortnightly' && item.fortnightlyPayDate) {
		const anchor = parseDateOnly(item.fortnightlyPayDate);
		if (!isWithinRange(anchor, start, end)) return [];
		return getFortnightlyDatesInRange(anchor, start, end);
	}

	if (item.frequency === 'four-weekly' && item.fourWeeklyPayDate) {
		const anchor = parseDateOnly(item.fourWeeklyPayDate);
		if (!isWithinRange(anchor, start, end)) return [];
		return getFourWeeklyDatesInRange(anchor, start, end);
	}

	if (item.frequency === 'weekly' && item.weeklyDay !== undefined) {
		return getWeeklyDatesInRange(start, end, item.weeklyDay);
	}

	if (item.frequency === 'one-off' && item.oneOffDate) {
		const date = parseDateOnly(item.oneOffDate);
		return isWithinRange(date, start, end) ? [date] : [];
	}

	return [];
}

/**
 * `amount × occurrencesInRange(...)`. The single function callers should
 * use when summing what a budget category or income source contributes
 * over a pay cycle.
 */
export function scheduleItemTotal(item: ScheduleItem, start: Date, end: Date): number {
	return occurrencesInRange(item, start, end).length * (item.amount ?? 0);
}

export interface ExpectedIncome {
	/** Total expected across the whole pay cycle. */
	expectedTotal: number;
	/**
	 * Portion expected on or before `now`: the whole cycle for a past month, the
	 * occurrences up to today for the current cycle, and zero for a future cycle.
	 */
	expectedToDate: number;
}

/**
 * Expected income for a pay-cycle month, split into the full-cycle total and
 * the amount due so far. Reuses occurrencesInRange so the per-frequency rules
 * live in one place — the dashboard previously re-implemented all four
 * frequency branches inline. `now` is injectable for testing.
 */
export function scheduledIncomeForCycle(
	schedules: ScheduleItem[],
	yearMonth: string,
	now: Date = new Date()
): ExpectedIncome {
	const { start, end } = getPayCycleRange(yearMonth);

	// "As of" cutoff for the to-date figure: null before the cycle starts
	// (future), the cycle end once it's over (past), otherwise today (current).
	const asOf = now < start ? null : now > end ? end : now;

	let expectedTotal = 0;
	let expectedToDate = 0;
	for (const schedule of schedules) {
		const amount = schedule.amount ?? 0;
		for (const date of occurrencesInRange(schedule, start, end)) {
			expectedTotal += amount;
			if (asOf && date <= asOf) expectedToDate += amount;
		}
	}
	return { expectedTotal: round2(expectedTotal), expectedToDate: round2(expectedToDate) };
}
